import JSEncrypt from 'jsencrypt';
import type { IResLogin } from './type';
import { Store, IStore } from './useLoginStore';
import { Pivot } from 'api';
import { getSessionId } from 'util/session';
import { getGraphicCaptchaUrl } from 'util/graphicCaptcha';

export interface IAuthAPI {
  getSessionId: () => Promise<string>;
  getPublicKey: () => Promise<{ code?: number; data: string }>;
  checkUserRegistered: (username: string) => Promise<{ code?: number; data: boolean }>;
  loginByPassword: (params: { username: string; password: string; sessionId: string; graphicCaptcha: string }) => Promise<{ code?: number; data: IResLogin }>;
  registerByPassword: (params: { username: string; password: string; confirmPassword: string }) => Promise<{ code?: number; data: IResLogin }>;
  resetPassword: (params: { newPassword: string; oldPassword: string; sessionId: string; graphicCaptcha: string }) => Promise<{ code?: number; data: any }>;
}

export interface IFormStore {
  name: string;
  mobile: string;
  password: string;
  confirmPassword: string;
  agreement: boolean;
  graphicCaptcha: string;
  newPassword: string;
}

export interface IStateStore {
  btnLoading: boolean;
  isUserRegistered: boolean;
  graphicCaptchaUrl: string;
  accountError: string;
  passwordError: string;
  graphicCaptchaError: string;
  newPasswordError: string;
}

class PrivAuth {
  static publicKey?: string;
  private _API: IAuthAPI;
  public formStore: IStore<IFormStore>;
  public stateStore: IStore<IStateStore>;

  constructor() {
    this._API = {
      getSessionId,
      checkUserRegistered: (data) => Pivot.checkUserRegistered(data).toPromise(),
      loginByPassword: (data) => Pivot.loginByRSA(data).toPromise(),
      registerByPassword: (data) => Pivot.registerByRSA(data).toPromise(),
      getPublicKey: () => Pivot.getRSAPublicKey().toPromise(),
      resetPassword: (data) => Pivot.modifyPasswordByRSA(data).toPromise()
    };
    this.formStore = Store<IFormStore>({
      name: '',
      mobile: '',
      password: '',
      confirmPassword: '',
      agreement: false,
      graphicCaptcha: '',
      newPassword: ''
    });
    this.stateStore = Store<IStateStore>({
      btnLoading: false,
      isUserRegistered: true,
      graphicCaptchaUrl: '',
      accountError: '',
      passwordError: '',
      graphicCaptchaError: '',
      newPasswordError: ''
    });
  }

  // 获取公钥
  private getPublicKey = async () => {
    if (!PrivAuth.publicKey) {
      const { data } = await this._API.getPublicKey();
      PrivAuth.publicKey = data;
    }
    if (!PrivAuth.publicKey) throw new Error('公钥获取失败');
    return PrivAuth.publicKey;
  };

  // 加密密码
  encryptPassword = async (passowrd: string): Promise<string> => {
    const encrypt = new JSEncrypt();
    // 获取公钥
    const publicKey = await this.getPublicKey();
    // 设置公钥
    encrypt.setPublicKey(publicKey);
    // 加密密码
    const encrypted = encrypt.encrypt(passowrd.trim());
    if (!encrypted) {
      throw new Error('密码加密失败');
    }
    return encrypted;
  };

  // 清除错误信息
  clearError() {
    this.stateStore.set({
      passwordError: '',
      graphicCaptchaError: '',
      accountError: '',
      newPasswordError: ''
    });
  }

  // 刷新图形验证码
  refrashGraphicCaptchaUrl = async (force?: boolean) => {
    try {
      if (!this.stateStore.value.graphicCaptchaUrl && !force) return;
      const url = await getGraphicCaptchaUrl();
      this.stateStore.set({ graphicCaptchaUrl: url });
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  // 账号输入框输入
  handleUserNameChange = async () => {
    this.clearError();
    this.stateStore.set({ isUserRegistered: true });
  };

  // 验证账号是否注册
  checkUserRegistered = async (): Promise<boolean> => {
    try {
      const res = await this._API.checkUserRegistered(this.formStore.value.mobile);
      if (res?.code === 4003000) throw Error(res as any);
      this.stateStore.set({ isUserRegistered: true });
      return true;
    } catch (err) {
      this.stateStore.set({
        isUserRegistered: false,
        accountError: '账号未注册'
      });
      this.formStore.set({ password: '', confirmPassword: '' });
      return false;
    }
  };

  // 密码登陆
  fetchPasswordLogin = async (): Promise<IResLogin | null> => {
    this.clearError();
    this.stateStore.set({ btnLoading: true });
    try {
      const sessionId = await this._API.getSessionId();
      const { mobile, password, graphicCaptcha } = this.formStore.value;
      const passwordEncrypted = await this.encryptPassword(password);
      const { data } = await this._API.loginByPassword({
        username: mobile,
        password: passwordEncrypted,
        sessionId,
        graphicCaptcha: graphicCaptcha
      });
      return data;
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      if (errorCode === 4001003) {
        this.refrashGraphicCaptchaUrl(true);
      } else if (errorCode === 4001004) {
        this.refrashGraphicCaptchaUrl(true);
        this.stateStore.set({
          graphicCaptchaError: '图形验证码错误'
        });
      } else if (errorCode === 4001000 || errorCode === 4001007) {
        this.refrashGraphicCaptchaUrl(false);
        this.stateStore.set({ passwordError: '账号或密码错误' });
      } else {
        this.refrashGraphicCaptchaUrl(false);
        this.stateStore.set({ passwordError: '未知错误' });
      }
      return null;
    } finally {
      this.stateStore.set({ btnLoading: false });
    }
  };

  // 密码注册
  fetchPasswordRegister = async (): Promise<IResLogin | null> => {
    this.clearError();
    this.stateStore.set({ btnLoading: true });

    try {
      const { mobile, password, confirmPassword } = this.formStore.value;
      if (password !== confirmPassword) {
        this.stateStore.set({ passwordError: '两次输入的密码不一致' });
        return null;
      }
      const passwordEncrypted = await this.encryptPassword(password);
      const { data } = await this._API.registerByPassword({
        username: mobile,
        password: passwordEncrypted,
        confirmPassword: passwordEncrypted
      });

      return data;
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      if (errorCode === 4001003) {
        this.refrashGraphicCaptchaUrl(true);
      } else if (errorCode === 4001004) {
        this.refrashGraphicCaptchaUrl(true);
        this.stateStore.set({
          graphicCaptchaError: '图形验证码错误'
        });
      } else if (errorCode === 4001000 || errorCode === 4001007) {
        this.refrashGraphicCaptchaUrl(false);
        this.stateStore.set({ passwordError: '账号或密码错误' });
      } else {
        this.refrashGraphicCaptchaUrl(false);
        this.stateStore.set({ passwordError: '未知错误' });
      }
      return null;
    } finally {
      this.stateStore.set({ btnLoading: false });
    }
  };

  // 修改密码
  fetchPasswordReset = async (): Promise<boolean> => {
    this.clearError();
    this.stateStore.set({ btnLoading: true });

    try {
      const sessionId = await this._API.getSessionId();
      const { password: oldPassword, newPassword, confirmPassword, graphicCaptcha } = this.formStore.value;

      if (!newPassword || !oldPassword || !confirmPassword) {
        throw new Error('密码不能为空');
      }

      const newPasswordEncrypted = await this.encryptPassword(newPassword);
      const oldPasswordEncrypted = await this.encryptPassword(oldPassword);

      await this._API.resetPassword({
        newPassword: newPasswordEncrypted,
        oldPassword: oldPasswordEncrypted,
        sessionId,
        graphicCaptcha: graphicCaptcha
      });

      return true;
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      const errorMsg = err?.response?.data?.message;

      // 账号系统逻辑，errorCode重复，而且后端改不动，只能判断message
      if (errorMsg === 'Password is invalid.') {
        this.stateStore.set({
          passwordError: '原密码错误'
        });
      } else if (errorCode === 400401004 || errorCode === 4003015 || errorCode === 4001007) {
        this.stateStore.set({
          newPasswordError: '设置密码失败，错误原因不能与上一次密码相同'
        });
      } else if (errorCode === 4001003) {
        this.stateStore.set({
          graphicCaptchaError: '请输入图形验证码'
        });
        this.refrashGraphicCaptchaUrl(true);
      } else if (errorCode === 4001004) {
        this.stateStore.set({
          graphicCaptchaError: '图形验证码错误'
        });
        this.refrashGraphicCaptchaUrl(true);
      } else {
        this.stateStore.set({ newPasswordError: '未知错误' });
      }

      return false;
    } finally {
      this.stateStore.set({ btnLoading: false });
    }
  };
}

export default PrivAuth;
